import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import {
  getContentById,
  getUserRole,
  voteContent,
  getContentVotes,
  getRandomContent,
  getContentBySlug,
  getNextPrevContentByOrder,
} from "../services/firebase";
import { ContentItem } from "../types";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import PunishmentDisplay from "../components/PunishmentDisplay--old";
import ContentCarousel from "../components/ContentCarousel";
import Navbar from "../components/Navbar";
import StyledLoadingSpinner from "../components/ui/StyledLoadingSpinner";
import MetaHead from "../components/ui/MetaHead";
import PunishmentReceipe from "../components/PunishmentDisplay";
import DraftIdeasRecipe from "../components/DraftIdeasRecipe";

const DraftIdeasContentPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<ContentItem | null>(null);
  const [carouselItems, setCarouselItems] = useState<ContentItem[]>([]);
  const { theme } = useTheme();
  const { user, userRole: userRoleFromAuth } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [upvotes, setUpvotes] = useState(0);
  const [downvotes, setDownvotes] = useState(0);
  const [userVote, setUserVote] = useState<"up" | "down" | null>(null);
  const [nextContent, setNextContent] = useState<ContentItem | null>(null);
  const [prevContent, setPrevContent] = useState<ContentItem | null>(null);

  useEffect(() => {
    if (userRoleFromAuth !== "admin") {
      navigate("/");
    }
  }, [userRoleFromAuth]);

  useEffect(() => {
    const fetchContent = async () => {
      if (!slug) return;

      try {
        let fetchedContent: ContentItem | null = await getContentBySlug(
          slug,
          "draftIdeas"
        );

        if (!fetchedContent) {
          fetchedContent = await getContentById(slug, "draftIdeas");

          if (fetchedContent?.slug) {
            navigate(`/draft-ideas/${fetchedContent.slug}`, { replace: true });
            return;
          }
        }

        if (fetchedContent) {
          let position = fetchedContent.socialMediaLinks
            ? Math.floor(fetchedContent.socialMediaLinks.length / 2)
            : 0;
          fetchedContent["socialMediaLinks"]?.splice(position, 0, {
            type: "custom",
            id: (Math.floor(Math.random() * 1000) + 1).toString(),
            url: "",
          });
          setContent(fetchedContent);
        } else {
          navigate("/draft-ideas");
        }
      } catch (error) {
        console.error("Error fetching content:", error);
      }
    };

    const fetchUserRole = async () => {
      if (user) {
        try {
          const role = await getUserRole(user.uid);
          setUserRole(role);
        } catch (error) {
          console.error("Error fetching user role:", error);
        }
      } else {
        setUserRole(null);
      }
    };

    const fetchCarouselItems = async () => {
      try {
        const items = await getRandomContent(6, false, "draftIdeas");
        setCarouselItems(items);
      } catch (error) {
        console.error("Error fetching carousel items:", error);
      }
    };

    fetchContent();
    fetchUserRole();
    fetchCarouselItems();
  }, [slug, user, navigate]);

  useEffect(() => {
    const fetchVotes = async () => {
      if (slug) {
        try {
          const votes = await getContentVotes(slug);
          setUpvotes(votes.upvotes);
          setDownvotes(votes.downvotes);
          if (user) {
            setUserVote(votes.userVotes[user.uid] || null);
          } else {
            setUserVote(null);
          }
        } catch (error) {
          console.error("Error fetching votes:", error);
        }
      }
    };

    fetchVotes();
  }, [slug, user]);

  useEffect(() => {
    const fetchNextContent = async (order: number) => {
      const [next, prev] = await Promise.all([
        getNextPrevContentByOrder(order, "next", "draftIdeas"), // Fetch next content
        getNextPrevContentByOrder(order, "previous", "draftIdeas"), // Fetch previous content
      ]);
      setNextContent(next);
      setPrevContent(prev);
    };
    if (content !== null) {
      fetchNextContent(content?.order || 0);
    }
  }, [content]);

  const handleVote = async (voteType: "up" | "down") => {
    if (
      !content ||
      !user ||
      (userRole !== "commissioner" && userRole !== "admin")
    ) {
      return;
    }

    try {
      const newVoteType = userVote === voteType ? null : voteType;
      const result = await voteContent(content.id, user.uid, newVoteType);
      setUpvotes(result.upvotes);
      setDownvotes(result.downvotes);
      setUserVote(result.userVote);
    } catch (error) {
      console.error("Error voting:", error);
    }
  };

  const handleCarouselItemClick = (item: ContentItem) => {
    if (item.slug) {
      navigate(`/draft-ideas/${item.slug}`);
    } else {
      navigate(`/draft-ideas/${item.id}`);
    }
  };

  if (!content) {
    return <div></div>;
  }

  return (
    <div className="pageOuterWrapper">
      {content && (
        <MetaHead
          title={`${content.title}`}
          description={`${
            content.subtitle ||
            content.imageDescription ||
            `Check out this ${content.title} fantasy football punishment`
          }. Find the perfect last place punishment for your fantasy league losers and make losing memorable.`}
          image={typeof content.image === "string" ? content.image : undefined}
          url={`https://fantasyloser.com/draft-ideas/${
            content.slug || content.id
          }`}
          type="article"
          keywords={[
            "fantasy football punishment",
            "fantasy football loser",
            content.title.toLowerCase(),
            ...content.tags,
            "last place punishment",
            "fantasy league punishment",
            "loser consequence",
            "fantasy football forfeit",
            "league punishment idea",
          ]}
        />
      )}
      <Navbar />
      <div className="mainContentWrap">
        <DraftIdeasRecipe
          content={content}
          userRole={userRole}
          nextContent={nextContent}
          prevContent={prevContent}
        />
      </div>
      <Footer />
    </div>
  );
};

export default DraftIdeasContentPage;
